import * as THREE from 'three';
import { hash2 } from '../core/rng';

/**
 * Procedural, tileable textures generated at load time. Everything in the
 * game is original and code-generated; no external texture assets.
 */

type RGB = [number, number, number];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smooth = (t: number) => t * t * (3 - 2 * t);

/** Periodic value noise: tiles every `period` lattice cells. */
function vnoise(x: number, y: number, period: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const w = (i: number) => ((i % period) + period) % period;
  const a = hash2(w(xi), w(yi), seed);
  const b = hash2(w(xi + 1), w(yi), seed);
  const c = hash2(w(xi), w(yi + 1), seed);
  const d = hash2(w(xi + 1), w(yi + 1), seed);
  const u = smooth(xf);
  const v = smooth(yf);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}

function fbm(x: number, y: number, period: number, seed: number, octaves = 4): number {
  let amp = 0.5;
  let sum = 0;
  let norm = 0;
  let p = period;
  let fx = x;
  let fy = y;
  for (let o = 0; o < octaves; o++) {
    sum += vnoise(fx, fy, p, seed + o * 17) * amp;
    norm += amp;
    amp *= 0.5;
    fx *= 2;
    fy *= 2;
    p *= 2;
  }
  return sum / norm;
}

/** Tileable cellular (Worley) noise returning [F1, F2, cellId]. */
function worley(x: number, y: number, period: number, seed: number): [number, number, number] {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  let f1 = 9;
  let f2 = 9;
  let id = 0;
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const cx = xi + ox;
      const cy = yi + oy;
      const wx = ((cx % period) + period) % period;
      const wy = ((cy % period) + period) % period;
      const px = cx + hash2(wx, wy, seed);
      const py = cy + hash2(wx, wy, seed + 1);
      const d = Math.hypot(px - x, py - y);
      if (d < f1) {
        f2 = f1;
        f1 = d;
        id = hash2(wx, wy, seed + 2);
      } else if (d < f2) f2 = d;
    }
  }
  return [f1, f2, id];
}

function mix(a: RGB, b: RGB, t: number): RGB {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

function hex(h: number): RGB {
  return [(h >> 16) & 255, (h >> 8) & 255, h & 255];
}

function make(size: number, fn: (u: number, v: number) => RGB, repeat = true): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const [r, g, b] = fn(x / size, y / size);
      const i = (y * size + x) * 4;
      img.data[i] = r;
      img.data[i + 1] = g;
      img.data[i + 2] = b;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  if (repeat) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
  }
  tex.anisotropy = 4;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.needsUpdate = true;
  return tex;
}

export type GroundKind =
  | 'grass' | 'dryGrass' | 'dirt' | 'rock' | 'sand' | 'snow' | 'ice' | 'cobble' | 'tiles' | 'crystal' | 'ash' | 'moss' | 'planks';

const S = 256;

const GENERATORS: Record<GroundKind, () => THREE.CanvasTexture> = {
  grass: () =>
    make(S, (u, v) => {
      const n = fbm(u * 6, v * 6, 6, 11);
      const blot = fbm(u * 14, v * 14, 14, 14, 3);
      const fine = fbm(u * 64, v * 64, 64, 15, 2);
      const tip = hash2(Math.floor(u * S), Math.floor(v * S), 16);
      let c = mix(hex(0x3b4828), hex(0x66723e), n);
      c = mix(c, hex(0x2c3820), Math.max(0, 0.5 - blot) * 0.9);
      c = mix(c, hex(0x7f8a4c), Math.max(0, fine - 0.5) * 0.8);
      return mix(c, hex(0x9aa05e), tip > 0.93 ? 0.35 : 0);
    }),
  dryGrass: () =>
    make(S, (u, v) => {
      const n = fbm(u * 6, v * 6, 6, 13);
      const blot = fbm(u * 16, v * 16, 16, 17, 3);
      const fine = fbm(u * 60, v * 60, 60, 18, 2);
      let c = mix(hex(0x6e6436), hex(0x9a8a52), n);
      c = mix(c, hex(0x5a5230), Math.max(0, 0.5 - blot) * 0.8);
      return mix(c, hex(0xb8a468), Math.max(0, fine - 0.55) * 0.9);
    }),
  dirt: () =>
    make(S, (u, v) => {
      const n = fbm(u * 5, v * 5, 5, 21);
      const n2 = fbm(u * 20, v * 20, 20, 23, 3);
      const [f1, , id] = worley(u * 22, v * 22, 22, 9);
      // Packed earth: light enough that critical paths read brighter than the stone around them.
      let c = mix(hex(0x5e4a36), hex(0x8f7454), n);
      c = mix(c, hex(0x6c5842), n2 * 0.5);
      if (f1 < 0.16) c = mix(c, mix(hex(0x463a30), hex(0x7a6c5c), id), (0.16 - f1) / 0.16 * 0.8);
      return c;
    }),
  rock: () =>
    make(S, (u, v) => {
      const n = fbm(u * 5, v * 5, 5, 31, 5);
      const [f1, f2] = worley(u * 7, v * 7, 7, 3);
      const crack = Math.min(1, (f2 - f1) * 6);
      let c = mix(hex(0x55575a), hex(0x8a8b88), n);
      c = mix(hex(0x2b2c2e), c, 0.35 + 0.65 * crack);
      return c;
    }),
  sand: () =>
    make(S, (u, v) => {
      const n = fbm(u * 6, v * 6, 6, 41);
      const ripple = Math.sin((v * 22 + fbm(u * 3, v * 3, 3, 2) * 3) * Math.PI * 2) * 0.5 + 0.5;
      const c = mix(hex(0xb8945e), hex(0xdcc08a), n * 0.7 + ripple * 0.3);
      return mix(c, hex(0x9a7646), hash2(Math.floor(u * S), Math.floor(v * S), 8) * 0.12);
    }),
  snow: () =>
    make(S, (u, v) => {
      const n = fbm(u * 6, v * 6, 6, 51);
      const sparkle = hash2(Math.floor(u * S), Math.floor(v * S), 2) > 0.985 ? 1 : 0;
      const c = mix(hex(0xb8c6d8), hex(0xeef3fa), n);
      return mix(c, [255, 255, 255], sparkle);
    }),
  ice: () =>
    make(S, (u, v) => {
      const n = fbm(u * 4, v * 4, 4, 61);
      const [f1, f2] = worley(u * 5, v * 5, 5, 12);
      const crack = Math.min(1, (f2 - f1) * 10);
      const c = mix(hex(0x5a86b0), hex(0xa8d0ec), n);
      return mix(hex(0xe0f2ff), c, 0.25 + 0.75 * crack);
    }),
  cobble: () =>
    make(S, (u, v) => {
      const [f1, f2, id] = worley(u * 10, v * 10, 10, 71);
      const edge = Math.min(1, (f2 - f1) * 5);
      const n = fbm(u * 12, v * 12, 12, 72);
      const stone = mix(hex(0x5c5a56), hex(0x8f8a80), id * 0.7 + n * 0.3);
      return mix(hex(0x2a2622), stone, smooth(Math.min(1, edge * 1.6)));
    }),
  tiles: () =>
    make(S, (u, v) => {
      const gu = (u * 4) % 1;
      const gv = (v * 4) % 1;
      const row = Math.floor(v * 4);
      const su = (u * 4 + (row % 2) * 0.5) % 1;
      const edge = Math.min(su, 1 - su, gv, 1 - gv) * 12;
      const id = hash2(Math.floor(u * 4 + (row % 2) * 0.5), row, 81);
      const n = fbm(u * 16, v * 16, 16, 82);
      const tile = mix(hex(0x8a7d6a), hex(0xb5a68c), id * 0.6 + n * 0.4);
      void gu;
      return mix(hex(0x3a332a), tile, smooth(Math.min(1, edge)));
    }),
  crystal: () =>
    make(S, (u, v) => {
      const n = fbm(u * 5, v * 5, 5, 91);
      const [f1, f2, id] = worley(u * 6, v * 6, 6, 92);
      const edge = Math.min(1, (f2 - f1) * 9);
      // Dark violet-grey basalt with glowing crystal seams (kept low-saturation so VFX own the colour).
      const c = mix(hex(0x221c2a), hex(0x42364e), n);
      return mix(mix(hex(0x9a4ed0), hex(0xc85aa8), id), c, 0.25 + 0.75 * edge);
    }),
  ash: () =>
    make(S, (u, v) => {
      const n = fbm(u * 6, v * 6, 6, 101);
      const [f1, f2] = worley(u * 8, v * 8, 8, 102);
      const crack = Math.min(1, (f2 - f1) * 12);
      const c = mix(hex(0x1e1a18), hex(0x3e3632), n);
      // Thin, dim ember seams: the charcoal reads first, the glow comes from VFX and lights.
      return mix(hex(0xb4461a), c, 0.3 + 0.7 * crack);
    }),
  moss: () =>
    make(S, (u, v) => {
      const n = fbm(u * 7, v * 7, 7, 111);
      const [f1, f2] = worley(u * 9, v * 9, 9, 112);
      const crack = Math.min(1, (f2 - f1) * 6);
      const c = mix(hex(0x3c4a2e), hex(0x5e6e3a), n);
      return mix(hex(0x4a4c48), c, 0.4 + 0.6 * crack);
    }),
  planks: () =>
    make(S, (u, v) => {
      const plank = Math.floor(u * 6);
      const pu = (u * 6) % 1;
      const grain = fbm(u * 6 + plank * 3.1, v * 40, 64, 121 + plank);
      const edge = Math.min(pu, 1 - pu) * 20;
      const c = mix(hex(0x5a3c22), hex(0x8a6038), grain * 0.8 + hash2(plank, 0, 122) * 0.2);
      return mix(hex(0x2a1a0e), c, smooth(Math.min(1, edge)));
    }),
};

const cache = new Map<GroundKind, THREE.CanvasTexture>();

export function groundTexture(kind: GroundKind): THREE.CanvasTexture {
  let t = cache.get(kind);
  if (!t) {
    t = GENERATORS[kind]();
    cache.set(kind, t);
  }
  return t;
}

/** Soft radial gradient sprite (particles, glows, blob shadows). */
export function radialTexture(inner = 'rgba(255,255,255,1)', outer = 'rgba(255,255,255,0)', size = 128): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, inner);
  g.addColorStop(1, outer);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
